import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { PointArray } from '@editor/interfaces/attribute'
import { LineStyle, PolylineProps } from '@editor/interfaces/canvas'
import { LINE_WIDTH_TO_GAP_RATIO } from '@editor/pages/projects/blueprint-editor/constants'
import { findCenter } from '@editor/services/Points'
import { calculatePolylineMidpoint } from '@editor/services/Polyline'
import { toMatrix4 } from '@editor/services/Shape'
import { useAppSelector } from '@editor/store/app'
import { useFrame, useThree } from '@react-three/fiber'
import { getContrastingColor } from '@uiw/react-color'
import { clamp, cloneDeep } from 'lodash'
import { useDeviceSelectors } from 'react-device-detect'
import { Group, PerspectiveCamera, Vector3 } from 'three'
import { Line2, LineGeometry, LineMaterial } from 'three-stdlib'
import { useThrottledCallback } from 'use-debounce'

import { PlanePoints } from '../../PolygonPlaneMeshV2/definitions'
import useConfigs from './useConfigs'
import useEdgeHighlight from './useEdgeHighlight'

interface SegmentLabelData extends ReturnType<typeof calculatePolylineMidpoint> {
  id: string
  label: string
}

const useObjects = (props: PolylineProps, configs: ReturnType<typeof useConfigs>) => {
  const {
    id,
    parentId,
    points,
    offsetTransformation,
    label,

    vertexAnchors,

    customCamera,
    isWorking,
    isEditing,
    isAdjustingColors,
    isOutlineContrastingColor,
  } = props
  const { camera: threeCamera } = useThree()
  const {
    labelConfig,
    segmentLabelsConfig,
    lineConfig,
    lineOutlineConfig,

    canBeSeen,
    canLineBeInteracted,

    isEndsOnly,
    isDraggingRef,
    dragStartPosRef,
    currentPositionsRef,
    editingAnchorRef,

    setPointIndexForMerge,
  } = configs
  const [selectors] = useDeviceSelectors(window.navigator.userAgent) as [{ isMobile: boolean; isTablet: boolean }]
  const { isMobile } = selectors
  const edgeHighlight = useEdgeHighlight(props, configs)
  const { edgeHighlightRef } = edgeHighlight

  // Refs
  const tempV3 = useRef<Vector3>(new Vector3())
  const groupRef = useRef<Group>(null)
  const hitboxLineRef = useRef<Line2>(undefined)
  const lineRef = useRef<Line2>(undefined)
  const outlineRef = useRef<Line2>(undefined)
  const labelMessage = useRef<string | null>(null)
  const currentAnchorPositionRef = useRef<PointArray>(undefined)
  const lastObjectCountRef = useRef<number>(points.length)

  // Store
  const currentAnchorPosition = useAppSelector((state) => state.toolUtil.core.currentAnchorPosition)
  const currentStrokeColor = useAppSelector((state) => state.toolUtil.core.currentStrokeColor)
  const currentFillColor = useAppSelector((state) => state.toolUtil.core.currentFillColor)
  const selectedElementIds = useAppSelector((state) => state.toolUtil.core.selectedElementIds)

  // States
  const [labelAdjustmentAngle, setLabelAdjustmentAngle] = useState<number>()

  // Derived values
  const offsetMatrix = useMemo(() => toMatrix4(offsetTransformation), [offsetTransformation])
  const offsettedPoints: PointArray[] = useMemo(
    () =>
      points.map((p) =>
        tempV3.current
          .set(...p)
          .applyMatrix4(offsetMatrix)
          .toArray(),
      ),
    [points, offsetMatrix],
  )

  /**
   * Get polyline points based on current anchor position and editing state.
   */
  const getPolylinePoints = useCallback(
    (currentPos: PointArray, anchorIndex: number | null): PointArray[] | null => {
      if (vertexAnchors?.getPoints) {
        return vertexAnchors.getPoints(currentPos, anchorIndex)
      }

      const pts = cloneDeep(offsettedPoints)

      // new polygon
      if (isWorking && currentPos && !isMobile) {
        pts.push(currentPos)
      }

      // adjusting a single vertex
      else if (anchorIndex !== null && currentPos) {
        pts[anchorIndex] = currentPos
      }

      // dragging the entire polygon
      else if (isEditing && anchorIndex === null) {
        if (!dragStartPosRef.current) {
          dragStartPosRef.current = currentPos
        } else {
          const dragOffset = [
            currentPos[0] - dragStartPosRef.current[0],
            currentPos[1] - dragStartPosRef.current[1],
            currentPos[2] - dragStartPosRef.current[2],
          ]
          pts.forEach((p) => {
            p[0] += dragOffset[0]
            p[1] += dragOffset[1]
            p[2] += dragOffset[2]
          })
        }
      }

      return pts
    },
    [offsettedPoints, isWorking, isMobile, dragStartPosRef, isEditing, vertexAnchors],
  )

  /**
   * Use states for polyline points for ID stability.
   */
  const [polylinePoints, setPolylinePoints] = useState<PlanePoints[]>(() =>
    offsettedPoints.map((p) => ({
      id: crypto.randomUUID(),
      point: new Vector3(...p),
    })),
  )

  /**
   * Checks if a vertex should be visible.
   */
  const isVertexVisible = useCallback(
    (anchorIndex: number) => {
      if (vertexAnchors === undefined || vertexAnchors.visible === false || !canBeSeen) return false
      if (isEndsOnly) {
        return anchorIndex === 0 || anchorIndex === polylinePoints.length - 1
      }
      return true
    },
    [isEndsOnly, polylinePoints.length, vertexAnchors, canBeSeen],
  )

  const center = useMemo(
    () => (offsettedPoints.length ? findCenter(offsettedPoints.map((p) => new Vector3(...p))) : null),
    [offsettedPoints],
  )
  const camera = customCamera || threeCamera

  /**
   * Calculate the label adjustment angle based on the first segment of the polyline.
   */
  const calculateLabelAdjustmentAngle = useCallback(
    (pts: PointArray[]) => {
      if (!camera || pts.length < 2) return

      // -- Figure out direction vector of first to second point
      const p1 = new Vector3().fromArray(pts[0])
      const p2 = new Vector3().fromArray(pts[1])
      const dir = p2.clone().sub(p1).normalize()

      // -- Get the direction relative to the camera
      const cameraMatrix = camera.matrix.toArray()
      const right = new Vector3().setFromMatrixColumn(camera.matrixWorld, 0).normalize()
      const x = dir.dot(right)
      const y = dir.dot(new Vector3(cameraMatrix[4], cameraMatrix[5], cameraMatrix[6]).normalize())

      // -- Set angle in radians
      setLabelAdjustmentAngle(Math.atan2(y, x))
    },
    [camera],
  )

  /**
   * Throttled update of label adjustment angle for real-time calls.
   */
  const updateLabelAdjustmentAngle = useThrottledCallback(calculateLabelAdjustmentAngle, 1000 / 15)

  /**
   * Calculate label data (position and total distance).
   */
  const calculateLabelData = useCallback(
    (pts: PointArray[]) => {
      // If there are segment labels, place the primary label at the start to show total distance
      if (segmentLabelsConfig) {
        // -- Total distance
        const allPointsV3 = pts.map((p) => (p instanceof Vector3 ? p.clone() : new Vector3().fromArray(p)))
        const totalDistance = allPointsV3.reduce<number>((collection, point, index) => {
          const pointsPair = [point, allPointsV3[index + 1]]
          if (!pointsPair[1]) return collection
          return collection + pointsPair[0].distanceTo(pointsPair[1])
        }, 0)

        // -- Calculate adjustment angle
        calculateLabelAdjustmentAngle(pts)

        return {
          point: pts[0],
          totalDistance,
        }
      }

      return calculatePolylineMidpoint(pts)
    },
    [segmentLabelsConfig, calculateLabelAdjustmentAngle],
  )

  /**
   * Calculate segment label data.
   */
  const calculateSegmentLabelData = useCallback(
    (pts: PointArray[], ids?: string[]) =>
      pts.reduce<SegmentLabelData[]>((acc, _, i) => {
        if (i === 0) return acc
        const segment = {
          id: ids?.[i - 1] || crypto.randomUUID(),
          label: segmentLabelsConfig?.generateLabel?.([pts[i - 1], pts[i]]) || '',
          ...calculatePolylineMidpoint([pts[i - 1], pts[i]]),
        }
        acc.push(segment)
        return acc
      }, []),
    [segmentLabelsConfig],
  )

  /**
   * Throttled update of segment label data for real-time calls.
   */
  const updateSegmentLabelData = useThrottledCallback((pts: PointArray[]) => {
    // we need to retain the IDs for existing segments for stability
    setSegmentLabelData((prev) =>
      calculateSegmentLabelData(
        pts,
        prev.map((p) => p.id),
      ),
    )
  }, 1000 / 30)

  // Label states
  const [labelData, setLabelData] = useState(() =>
    label && offsettedPoints.length > 1 ? calculateLabelData(offsettedPoints) : null,
  )
  const [segmentLabelData, setSegmentLabelData] = useState<SegmentLabelData[]>(
    calculateSegmentLabelData(offsettedPoints),
  )

  /**
   * Update current anchor position ref
   */
  useEffect(() => {
    currentAnchorPositionRef.current = currentAnchorPosition
  }, [currentAnchorPosition])

  const [lineGeometry, setLineGeometry] = useState(() => {
    const len = polylinePoints.length
    const pts = polylinePoints
      .concat(
        { id: crypto.randomUUID(), point: new Vector3().fromArray(offsettedPoints[0] || [0, 0, 0]) },
        {
          id: crypto.randomUUID(),
          point: new Vector3().fromArray(offsettedPoints[1] || offsettedPoints[0] || [0, 0, 0]),
        },
      )
      .slice(0, Math.max(2, len)) // LineGeometry needs at least 2 points

    const geometry = new LineGeometry()
    geometry.setPositions(pts.map((p) => p.point.toArray()).flat())
    return geometry
  })

  /**
   * Update label position and message.
   * Throttled for performance.
   */
  const updateLabel = useThrottledCallback(
    (pts: PointArray[]) => {
      if (label && pts.length > 1) {
        const midpoint = calculateLabelData(pts)

        // Don't update if the distance has not exceeded 1mm to prevent overlapping
        if (midpoint.totalDistance < 0.001) return

        setLabelData(midpoint)
        if (labelConfig?.generateLabel) {
          labelMessage.current = labelConfig.generateLabel(pts)
        }
      }
    },
    1000 / 30,
    { leading: true, trailing: true },
  )

  /**
   * Update line geometry when it changes.
   */
  useEffect(() => {
    if (lineRef.current) {
      lineRef.current.geometry = lineGeometry
      lineRef.current.computeLineDistances()

      if (outlineRef.current) {
        outlineRef.current.geometry = lineRef.current.geometry.clone()
        outlineRef.current.computeLineDistances()
      }

      if (hitboxLineRef.current) {
        hitboxLineRef.current.geometry = lineRef.current.geometry.clone()
        hitboxLineRef.current.computeLineDistances()
      }
    }
  }, [lineGeometry])

  /**
   * Update polyline object geometry.
   *
   * @param pts - New points for the polyline
   */
  const updateObject = useCallback((pts: PointArray[]) => {
    // Update geometry if the number of points has increased
    if (pts.length !== lastObjectCountRef.current) {
      setLineGeometry((prev) => {
        if (prev) prev.dispose()
        const geometry = new LineGeometry()
        geometry.setPositions(pts.map((p) => p).flat())
        return geometry
      })
      lastObjectCountRef.current = pts.length
    } else {
      if (hitboxLineRef.current) {
        hitboxLineRef.current.geometry.setPositions(pts.map((p) => p).flat())
        hitboxLineRef.current.geometry.computeVertexNormals()
        hitboxLineRef.current.computeLineDistances()
      }

      if (lineRef.current) {
        lineRef.current.geometry.setPositions(pts.map((p) => p).flat())
        lineRef.current.geometry.computeVertexNormals()
        lineRef.current.computeLineDistances()
      }

      if (outlineRef.current) {
        outlineRef.current.geometry.setPositions(pts.map((p) => p).flat())
        outlineRef.current.geometry.computeVertexNormals()
        outlineRef.current.computeLineDistances()
      }
    }
  }, [])

  /**
   * Update main line
   */
  useEffect(() => {
    if (
      (!lineRef.current && !outlineRef.current) ||
      !currentAnchorPosition ||
      !canBeSeen ||
      (!isEditing && !isWorking) ||
      (isEditing && editingAnchorRef.current === null && isDraggingRef.current === false)
    )
      return

    const pts = getPolylinePoints(currentAnchorPosition, editingAnchorRef.current)
    if (!pts) return

    // -- Update mesh
    currentPositionsRef.current = pts
    updateObject(pts)

    // -- Update line / anchor
    setPolylinePoints((prev) =>
      pts.map((p, i) =>
        prev[i] ? { ...prev[i], point: new Vector3(...p) } : { id: crypto.randomUUID(), point: new Vector3(...p) },
      ),
    )
    updateLabel(pts)
    updateSegmentLabelData(pts)

    // -- Callbacks
    if (vertexAnchors?.onUpdate) {
      vertexAnchors.onUpdate(pts.map((p) => p))
    }
  }, [
    currentAnchorPosition,
    editingAnchorRef,
    isEditing,
    isWorking,
    canBeSeen,
    vertexAnchors,
    isDraggingRef,
    currentPositionsRef,
    updateLabel,
    updateSegmentLabelData,
    updateObject,
    getPolylinePoints,
  ])

  /**
   * Update polyline when points prop changes.
   */
  useEffect(() => {
    // don't update points while editing
    if (isEditing) return

    // Using `getPolylinePoints` will work nicely on desktop as it adds current anchor position,
    // but on touch devices, you don't want to add the current anchor position when points prop changes.
    const pts =
      isMobile || !currentAnchorPositionRef.current
        ? offsettedPoints
        : getPolylinePoints(currentAnchorPositionRef.current, null)
    if (!pts?.length) return

    // -- Reset point index for merging if the object count has changed
    if (offsettedPoints.length !== lastObjectCountRef.current) {
      setPointIndexForMerge(null)
    }

    /// -- Update points
    setPolylinePoints((prev) =>
      pts.map((p, i) =>
        prev[i] ? { ...prev[i], point: new Vector3(...p) } : { id: crypto.randomUUID(), point: new Vector3(...p) },
      ),
    )

    // -- Update mesh and labels
    updateObject(pts)
    if (segmentLabelsConfig) updateSegmentLabelData(pts)
    if (label) updateLabel(pts)
  }, [
    offsettedPoints,
    isEditing,
    isMobile,
    segmentLabelsConfig,
    label,
    updateLabel,
    getPolylinePoints,
    setPointIndexForMerge,
    updateObject,
    updateSegmentLabelData,
  ])

  /**
   * Update the mesh material color when the current fill color changes.
   * This is used for real-time updates of the fill color while the user is selecting it to
   * avoid unnecessary re-renders.
   *
   * The color set here _should not_ be the final color. It still must come from the `color` prop.
   */
  useEffect(() => {
    if (
      !lineRef.current ||
      !(lineRef.current.material instanceof LineMaterial) ||
      !currentStrokeColor ||
      (!parentId && !selectedElementIds.includes(id) && !isWorking) ||
      (parentId && !selectedElementIds.includes(parentId) && !isWorking)
    )
      return

    lineRef.current.material.color.set(currentStrokeColor)
  }, [lineRef, currentStrokeColor, selectedElementIds, id, parentId, isWorking])
  useEffect(() => {
    if (
      !lineRef.current ||
      !(lineRef.current.material instanceof LineMaterial) ||
      !currentFillColor ||
      !isAdjustingColors
    )
      return

    lineRef.current.material.color.set(currentFillColor)
    if (isOutlineContrastingColor && outlineRef.current) {
      const lineBgColor = getContrastingColor(currentFillColor)
      const outlineColor = lineBgColor === '#000000' ? '#888' : '#eee'
      outlineRef.current.material.color.set(outlineColor)
    }
  }, [currentFillColor, isAdjustingColors, isOutlineContrastingColor])
  useEffect(() => {
    if (
      !lineRef.current ||
      !(lineRef.current.material instanceof LineMaterial) ||
      (!parentId && !selectedElementIds.includes(id) && !isWorking) ||
      (parentId && !selectedElementIds.includes(parentId) && !isWorking)
    )
      return

    lineRef.current.material.dashed = lineConfig?.style === LineStyle.Dashed
  }, [lineRef, lineConfig, selectedElementIds, id, parentId, isWorking])

  /**
   *  To keep the anchor size constant when zooming in/out.
   *  Use the camera's directional distance to prevent the anchor from appearing larger as it goes to the edge of the screen.
   *  NOTE: If this is heavy, consider using a distance from the camera to the ABControls' gizmo point for all anchors.
   *        The results will vary, but they look good.
   */
  useFrame(() => {
    if (camera instanceof PerspectiveCamera && center) {
      // distance from the camera to the anchor point
      const cameraToAnchor = center.clone().sub(camera.position.clone())
      // angle between the camera's forward vector and the vector from the camera to the anchor point
      const angle = cameraToAnchor.angleTo(new Vector3(0, 0, -1).applyQuaternion(camera.quaternion.clone()))
      // center position of viewing plane calculated from the camera's position and the distance to the anchor point
      const centerOfTheAnchorViewingPlane = camera.position
        .clone()
        .sub(new Vector3(0, 0, cameraToAnchor.length() * Math.cos(angle)).applyQuaternion(camera.quaternion.clone()))
      const scaleFactor = clamp(camera.position.distanceTo(centerOfTheAnchorViewingPlane) * 6, 1, 10) / 10

      // gradually increase line width by scaleFactor, thinner line at low scaleFactor, thicker line at high scaleFactor
      if (lineRef.current && lineConfig) {
        const lineWidth = lineConfig.thickness + scaleFactor ** 2 * 0.005
        lineRef.current.material.linewidth = lineWidth
        lineRef.current.material.dashSize = lineRef.current.material.linewidth * LINE_WIDTH_TO_GAP_RATIO
        lineRef.current.material.gapSize = lineRef.current.material.linewidth * LINE_WIDTH_TO_GAP_RATIO
      }

      if (outlineRef.current && lineOutlineConfig) {
        outlineRef.current.material.linewidth =
          lineOutlineConfig.thickness + Math.max(0.0025, lineOutlineConfig.thickness * 0.5) + scaleFactor ** 2 * 0.01
        outlineRef.current.material.dashSize = outlineRef.current.material.linewidth * LINE_WIDTH_TO_GAP_RATIO
        outlineRef.current.material.gapSize = outlineRef.current.material.linewidth * LINE_WIDTH_TO_GAP_RATIO

        if (edgeHighlightRef.current) {
          edgeHighlightRef.current.material.linewidth = outlineRef.current.material.linewidth
        }
      }

      if (hitboxLineRef.current && (lineConfig || lineOutlineConfig)) {
        if (canLineBeInteracted) {
          hitboxLineRef.current.material.linewidth =
            (lineOutlineConfig?.thickness || lineConfig?.thickness || 0) +
            Math.max(
              0.0025,
              Math.max(lineRef.current?.material.linewidth || 0, outlineRef.current?.material.linewidth || 0) * 0.5,
            ) +
            scaleFactor ** 2 * 0.02
          hitboxLineRef.current.material.dashSize = hitboxLineRef.current.material.linewidth * LINE_WIDTH_TO_GAP_RATIO
          hitboxLineRef.current.material.gapSize = hitboxLineRef.current.material.linewidth * LINE_WIDTH_TO_GAP_RATIO
        } else {
          // A hacky workaround to make the hitbox line not interfere with vertex/anchor mouse events
          hitboxLineRef.current.material.linewidth = 0.00001
        }
      }

      if (segmentLabelsConfig && !isEditing) updateLabelAdjustmentAngle(offsettedPoints)
    }
  })

  return {
    ...edgeHighlight,

    groupRef,
    hitboxLineRef,
    lineRef,
    outlineRef,
    editingAnchorRef,

    currentAnchorPosition,
    polylinePoints,
    isVertexVisible,

    labelData,
    labelMessage,
    segmentLabelData,
    labelAdjustmentAngle,
  }
}

export default useObjects
